import { LitElement, html, css, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';
// import {classMap} from 'lit/directives/class-map.js';
import { bindEvent, Handler } from '@gsmlg/utils/dist/browser/bindEvent';
import '../icons/bars';

@customElement('app-navigator')
class AppNavigatorElement extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: var(--app-navigator-color, #362424);
      background-color: var(--app-navigator-background-color, transparent);
    }
    :host(.fixed) {
      position: fixed;
      top: 0;
      left: 0;
    }
    :host(.hide) {
      display: none;
    }

    header {
      display: none;
    }

    @media only screen and (max-width: 992px) {
      :host {
        flex-direction: row;
        width: 100%;
        height: 2.5rem;
        position: fixed;
        bottom: 0px;
        left: 0px;
        right: 0px;
      }
      :host(.fixed) {
        position: fixed;
        top: unset;
      }
    }

    @media only screen and (min-width: 992px) {
      :host {
        display: flex;
        flex-direction: column;
        width: var(--app-navigator-width, 14rem);
        height: 100%;
      }

      header {
        display: flex;
        width: 100%;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }
  `;

  @property({ type: Boolean, attribute: 'fixed', reflect: true })
  fixed: boolean = false;

  @property({ type: Boolean, attribute: 'control-parent', reflect: true })
  controlParent: boolean = false;

  @property({ type: String, attribute: 'app-name', reflect: true })
  appName: string = 'GSMLG.DEV';

  @property({ type: String, attribute: 'color', reflect: true })
  color: string | undefined = undefined;

  @property({ type: String, attribute: 'background-color', reflect: true })
  backgroundColor: string | undefined = undefined;

  _elHandlers: Handler[] = [];

  isMobile: boolean = false;

  menuIsOpen: boolean = false;

  constructor() {
    super();

    this.toggleMenuOpen.bind(this);
    this._setColor();
  }

  override connectedCallback() {
    super.connectedCallback();
    this._elHandlers.push(this.handleScroll());
    this._elHandlers.push(this.handleMenuWhenMQChange());
    this.updateParentMarginLeft(this.fixed);
  }

  override disconnectedCallback() {
    if (this._elHandlers.length > 0) {
      this._elHandlers.forEach(({ release }) => release());
    }
    this.updateParentMarginLeft(false);

    super.disconnectedCallback();
  }

  protected override firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties);
    this._setColor();
  }

  override attributeChangedCallback(
    name: string,
    old: string | null,
    value: string | null,
  ) {
    super.attributeChangedCallback(name, old, value);
    if (name === 'fixed') {
      if (this.fixed) {
        this.classList.add('fixed');
      } else {
        this.classList.remove('fixed');
      }
      this.updateParentMarginLeft(this.fixed);
    }
    if (name === 'control-parent') {
      this.updateParentMarginLeft(this.controlParent ? this.fixed : false);
    }
    if (name === 'color') {
      this.style.color = this.color as string;
    }
    if (name === 'background-color') {
      this.style.backgroundColor = this.backgroundColor as string;
    }
    this.requestUpdate();
  }

  override render() {
    return html`
      <div>
        <header>
          <slot name="logo"></slot>
          <h3>${this.appName}</h3>
        </header>
        <div>
          <slot name="menus"></slot>
        </div>
      </div>
    `;
  }

  private handleMenuWhenMQChange() {
    this.isMobile = !window.matchMedia('(min-width: 992px)').matches;
    return bindEvent(window.matchMedia('(min-width: 992px)'), 'change', ({ matches }) => {
      if (matches) {
        this.menuIsOpen = false;
        this.isMobile = false;
        this.updateParentMarginLeft(this.fixed);
      } else {
        this.isMobile = true;
        this.updateParentMarginLeft(false);
      }
      this.classList.remove('hide');
      this.requestUpdate();
    });
  }

  private updateParentMarginLeft(fixed: boolean) {
    if (fixed && this.controlParent && this.parentElement) {
      this.parentElement.style.marginLeft = window.getComputedStyle(this).width;
    } else if (this.parentElement) {
      this.parentElement.style.marginLeft = '';
    }
  }

  private _setColor() {
    if (this.color) {
      this.style.color = this.color;
    }
    if (this.backgroundColor) {
      this.style.backgroundColor = this.backgroundColor;
    }
  }

  private handleScroll() {
    if (this.parentElement) {
      let lastKnownScrollPosition = window.scrollY;
      return bindEvent(window, 'scroll', (_evt: Event) => {
        if (this.isMobile) {
          if (lastKnownScrollPosition < window.scrollY) {
            this.classList.add('hide');
          } else {
            this.classList.remove('hide');
          }
        }
        lastKnownScrollPosition = window.scrollY;
      });
    }
    return {
      release: () => {},
    };
  }

  private toggleMenuOpen() {
    this.menuIsOpen = !this.menuIsOpen;
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-navigator': AppNavigatorElement;
  }
}
